import { useState, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export const useSystemAudioCapture = () => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [hasVirtualDevice, setHasVirtualDevice] = useState(false);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const { toast } = useToast();

  // Detect virtual audio devices
  useEffect(() => {
    const detectVirtualDevice = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        
        const virtualDeviceNames = ['vb-cable', 'blackhole', 'loopback', 'virtual', 'soundflower'];
        const hasVirtual = audioInputs.some(device => 
          virtualDeviceNames.some(name => device.label.toLowerCase().includes(name))
        );
        
        setHasVirtualDevice(hasVirtual);
      } catch (err) {
        console.error('Error detecting audio devices:', err);
      }
    };

    detectVirtualDevice();
  }, []);

  const setupAudioAnalyser = (stream: MediaStream) => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const updateLevel = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);
      
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    
    updateLevel();
  };

  const captureSystemAudio = async (): Promise<MediaStream | null> => {
    try {
      // Try virtual audio device first if available
      if (hasVirtualDevice) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        
        const virtualDeviceNames = ['vb-cable', 'blackhole', 'loopback', 'virtual', 'soundflower'];
        const virtualDevice = audioInputs.find(device => 
          virtualDeviceNames.some(name => device.label.toLowerCase().includes(name))
        );

        if (virtualDevice) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: virtualDevice.deviceId }
          });
          
          toast({
            title: "System audio connected",
            description: `Using ${virtualDevice.label}`,
          });
          
          return stream;
        }
      }

      // Fallback: Use getDisplayMedia with audio
      toast({
        title: "Select audio source",
        description: "Choose a browser tab or window to share its audio",
      });

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });

      // Extract only audio track
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const audioStream = new MediaStream([audioTrack]);
        
        // Stop video tracks since we only want audio
        stream.getVideoTracks().forEach(track => track.stop());
        
        return audioStream;
      }

      toast({
        title: "No audio available",
        description: "The selected source doesn't provide audio",
        variant: "destructive"
      });
      
      stream.getTracks().forEach(track => track.stop());
      return null;

    } catch (err) {
      console.error('Error capturing system audio:', err);
      
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        toast({
          title: "Permission denied",
          description: "Audio capture was cancelled or denied",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Capture failed",
          description: "Could not capture system audio",
          variant: "destructive"
        });
      }
      
      return null;
    }
  };

  const startCapture = async () => {
    const stream = await captureSystemAudio();
    
    if (stream) {
      audioStreamRef.current = stream;
      setupAudioAnalyser(stream);
      setIsCapturing(true);

      // Handle track end
      stream.getTracks()[0].onended = () => {
        stopCapture();
      };
    }
  };

  const stopCapture = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    analyserRef.current = null;
    setIsCapturing(false);
    setAudioLevel(0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCapture();
    };
  }, []);

  return {
    isCapturing,
    audioLevel,
    audioStream: audioStreamRef.current,
    hasVirtualDevice,
    startCapture,
    stopCapture
  };
};
